import React, { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Download, Fuel, Loader2, Upload } from "lucide-react";
import { fmtNumber } from "@/lib/format";

const fmtD = (s) => (s && /^\d{4}-\d{2}-\d{2}/.test(s) ? s.slice(0, 10).split("-").reverse().join("/") : s || "-");

const downloadUnmatched = (rows) => {
  const un = rows.filter((r) => !r.matched);
  if (!un.length) return toast.info("No unmatched fills");
  const esc = (v) => { const s = v === undefined || v === null ? "" : String(v); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
  const lines = [["SL", "File Row", "Fill Date", "Truck", "Litres", "Amount", "Reason"].join(","),
    ...un.map((r, i) => [i + 1, r.row, fmtD(r.date), r.vehicle_number, r.litres, r.amount || 0, r.reason].map(esc).join(","))];
  const blob = new Blob(["\ufeff" + lines.join("\n")], { type: "text/csv;charset=utf-8" });
  const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `Diesel_Unmatched_${new Date().toISOString().slice(0, 10)}.csv`; a.click(); URL.revokeObjectURL(a.href);
};

function PreviewTable({ rows, sel, toggle }) {
  const newQty = {};
  const newAmt = {};
  rows.forEach((r, i) => {
    if (sel[i] && r.matched) {
      newQty[r.challan.id] = (newQty[r.challan.id] || 0) + r.litres;
      newAmt[r.challan.id] = (newAmt[r.challan.id] || 0) + (r.amount || 0);
    }
  });
  return (
    <div className="overflow-auto max-h-[52vh] rounded-lg border border-border">
      <table className="w-full text-sm">
        <thead className="bg-muted text-xs uppercase text-muted-foreground sticky top-0 z-10">
          <tr>{["", "Fill Date", "Truck", "Litres", "Amount ₹", "Matched Trip", "Gap", "Delivery No", "Current Fuel Qty", "New Fuel Qty", "Current Diesel ₹", "New Diesel ₹"].map((h) => <th key={h} className="py-2 px-3 text-left font-semibold">{h}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((r, i) => {
            const cur = r.challan?.fuel_qty || 0;
            const nq = r.matched ? newQty[r.challan.id] : undefined;
            const changed = r.matched && nq !== undefined && cur && Math.abs(cur - nq) > 0.009;
            return (
              <tr key={i} className={`border-t border-border ${r.matched ? (changed ? "bg-amber-500/10" : "") : "bg-rose-500/[0.06] text-muted-foreground"}`} data-testid={`diesel-row-${i}`}>
                <td className="py-2 px-3">{r.matched && <Checkbox checked={!!sel[i]} onCheckedChange={() => toggle(i)} data-testid={`diesel-check-${i}`} />}</td>
                <td className="py-2 px-3 font-mono">{fmtD(r.date)}</td>
                <td className="py-2 px-3 font-mono">{r.vehicle_number || "-"}</td>
                <td className="py-2 px-3 text-right font-mono">{fmtNumber(r.litres)}</td>
                <td className="py-2 px-3 text-right font-mono" data-testid={`diesel-amount-${i}`}>{fmtNumber(r.amount || 0)}</td>
                <td className="py-2 px-3 font-mono">{r.matched ? fmtD(r.challan.gr_date) : <span className="text-rose-500 font-sans">{r.reason}</span>}</td>
                <td className="py-2 px-3">{r.matched ? (r.gap_days === 0 ? "same day" : `${r.gap_days > 0 ? "+" : ""}${r.gap_days}d`) : ""}</td>
                <td className="py-2 px-3 font-mono text-xs">{r.challan?.delivery_number || ""}</td>
                <td className={`py-2 px-3 text-right font-mono ${changed ? "text-amber-600 font-semibold" : ""}`}>{r.matched ? fmtNumber(cur) : ""}</td>
                <td className="py-2 px-3 text-right font-mono font-semibold">{r.matched ? (nq !== undefined ? fmtNumber(nq) : <span className="text-muted-foreground font-normal">unchanged</span>) : ""}</td>
                <td className="py-2 px-3 text-right font-mono">{r.matched ? fmtNumber(r.challan.diesel || 0) : ""}</td>
                <td className="py-2 px-3 text-right font-mono font-semibold" data-testid={`diesel-new-amount-${i}`}>{r.matched ? (nq !== undefined ? fmtNumber(newAmt[r.challan.id]) : <span className="text-muted-foreground font-normal">unchanged</span>) : ""}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export function DieselMatchDialog({ hubId, onApplied }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState(null);
  const [sel, setSel] = useState({});
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file); fd.append("hub", hubId);
      const { data: d } = await api.post("/diesel-match/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setData(d);
      setSel(Object.fromEntries(d.rows.map((r, i) => [i, r.matched])));
    } catch (e) { const det = e?.response?.data?.detail; toast.error(typeof det === "string" ? det : "Could not read the file"); }
    setBusy(false);
  };
  const apply = async () => {
    const byId = {};
    data.rows.forEach((r, i) => {
      if (sel[i] && r.matched) {
        const p = byId[r.challan.id] || { fuel_qty: 0, diesel: 0 };
        byId[r.challan.id] = { fuel_qty: p.fuel_qty + r.litres, diesel: p.diesel + (r.amount || 0) };
      }
    });
    const items = Object.entries(byId).map(([challan_id, v]) => ({ challan_id, fuel_qty: v.fuel_qty, ...(data.has_amount && v.diesel > 0 ? { diesel: v.diesel } : {}) }));
    if (!items.length) return toast.error("Select at least one matched fill");
    setBusy(true);
    try {
      const { data: res } = await api.post("/diesel-match/apply", { hub: hubId, items });
      toast.success(`${data.has_amount ? "Fuel Qty & Diesel ₹" : "Fuel Qty"} updated on ${res.updated} trips`);
      setOpen(false); setData(null); onApplied?.();
    } catch (e) { const det = e?.response?.data?.detail; toast.error(typeof det === "string" ? det : "Could not apply"); }
    setBusy(false);
  };
  const picked = data ? data.rows.filter((r, i) => sel[i] && r.matched) : [];
  return (
    <>
      <Button variant="outline" className="gap-2" onClick={() => setOpen(true)} data-testid="diesel-upload-open"><Fuel className="h-4 w-4" />Upload Diesel File</Button>
      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setData(null); }}>
        <DialogContent className="max-w-5xl" data-testid="diesel-match-dialog">
          <DialogHeader><DialogTitle>Match Diesel File to Dispatch · Dalmia Bulk</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Each fill is matched to the same truck's nearest dispatch date within 3 days. <b>Fuel Qty (litres)</b> and <b>Diesel ₹</b> (from the file's Amount column) are updated. Amber rows already have a Fuel Qty that will change.</p>
          <label className="flex items-center justify-center gap-3 rounded-xl border-2 border-dashed border-border p-5 cursor-pointer hover:bg-accent/40 transition-colors" data-testid="diesel-file-drop">
            {busy && !data ? <Loader2 className="h-5 w-5 animate-spin" /> : <Upload className="h-5 w-5 text-muted-foreground" />}
            <span className="text-sm">{data ? "Upload a different file" : "Choose pump Excel / CSV (Date, Vehicle, Litres)"}</span>
            <input type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} data-testid="diesel-file-input" />
          </label>
          {data && <>
            <div className="flex gap-4 text-sm" data-testid="diesel-summary">
              <span><b className="text-emerald-600">{data.matched}</b> matched</span>
              <span><b className="text-rose-500">{data.total - data.matched}</b> unmatched</span>
              <button type="button" onClick={() => downloadUnmatched(data.rows)} className="inline-flex items-center gap-1 text-rose-500 hover:underline" data-testid="diesel-download-unmatched"><Download className="h-3.5 w-3.5" />Download unmatched</button>
              <span><b>{picked.length}</b> selected · {fmtNumber(picked.reduce((s, r) => s + r.litres, 0))} L · ₹{fmtNumber(picked.reduce((s, r) => s + (r.amount || 0), 0))}</span>
            </div>
            <PreviewTable rows={data.rows} sel={sel} toggle={(i) => setSel((p) => ({ ...p, [i]: !p[i] }))} />
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setOpen(false)} data-testid="diesel-cancel">Cancel</Button>
              <Button onClick={apply} disabled={busy || !picked.length} data-testid="diesel-apply">{busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}Apply to {new Set(picked.map((r) => r.challan.id)).size} trips</Button>
            </div>
          </>}
        </DialogContent>
      </Dialog>
    </>
  );
}
